// ISSUE DEPOSIT ADDRESS (UI rebuild batch 4 · custody setup · reference build) — Astra's focused surface with three states
// kept apart: issuing, verifying (this browser re-derives the address from the registered key), ready. The QR and Copy
// follow the check (components/v2/custody-admin/verify.tsx); a mismatch offers neither.
//
// Deliberately NOT Astra's Party / Deal fields and "Review issuance": the operator's own ruling (18 and 21 Oct 2026) is
// ONE tap, no customer and no deal to type — the customer is tagged when the money lands, once (Deposit addresses → the
// address → Tag, or the bot's Tag customer). Issuing moves no money and is not irreversible, so there is no readback.
// Kept from screens/custody/Addresses.tsx: newDepositAddress(note), its refusal words (no_key), the list's refresh.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { custody as api } from '../../../lib/api';
import { ApiError } from '../../../lib/errors';
import { Field } from '../../../components/Field';
import { StateLine, Fact } from '../../../components/v2/kit';
import { FormFrame, FormFoot, FormAside, PRIMARY, QUIET } from '../../../components/v2/move/FormFrame';
import { GiveOut, checkOf } from '../../../components/v2/custody-admin/verify';
import { CA } from '../../../components/v2/custody-admin/paths';

const textOf = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);

function issueRefusal(err: unknown): { line: string; more: string; noKey: boolean } {
  const reason = err instanceof ApiError && typeof err.body.reason === 'string' ? err.body.reason : null;
  if (reason === 'no_key') return { line: 'No deposit key yet — register account 1 first.', more: 'Register your Ledger\'s account 1 in Devices & keys, then try again.', noKey: true };
  return { line: "Couldn't issue an address — try again.", more: textOf(err), noKey: false };
}

const FIGURE = { idle: 'Required', derived: 'Checked', unchecked: 'Not checked', mismatch: 'Mismatch' } as const;

export function IssueAddressV2() {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const issue = useMutation({
    mutationFn: () => api.newDepositAddress(note.trim() || undefined),
    onSuccess: () => { setNote(''); void qc.invalidateQueries({ queryKey: ['custody', 'deposit-addresses'] }); },
  });
  const fresh = issue.data ?? null;
  const check = fresh ? checkOf(fresh) : null;
  const refusal = issue.isError ? issueRefusal(issue.error) : null;

  return (
    <FormFrame
      title="Issue deposit address"
      aside={(
        <FormAside
          title="Address verification"
          figure={FIGURE[check ?? 'idle']}
          help={<p>A fresh address from the registered account 1 key, the same one the wallet bot&rsquo;s /new issues. This browser derives it again from the key it kept at registration; only an address it derives gets a QR. The customer is tagged once, when the money lands.</p>}
        />
      )}
      below={<Link to="/custody/deposits?show=addresses" className="btn-link text-meta">All deposit addresses</Link>}
    >
      {!fresh ? (
        <>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Field label="Network"><p className="py-2 text-body">USDT·TRC</p></Field>
            {/* The phone has no aside: the verification state stays a labelled fact here (Astra, 2 Nov 2026). */}
            <div className="xl:hidden"><Field label="Address verification"><p className="py-2 text-body">{FIGURE[check ?? 'idle']}</p></Field></div>
            <Field label="Note (optional)">
              <input className="input" value={note} maxLength={200} placeholder="ali 5k" onChange={(e) => setNote(e.target.value)} disabled={issue.isPending} />
            </Field>
          </div>
          {refusal && (
            <StateLine tone="bad" more={refusal.more} action={refusal.noKey ? <Link to={CA.register} className="btn-link text-meta">Register device</Link> : undefined}>
              {refusal.line}
            </StateLine>
          )}
          <FormFoot note="Key · account 1">
            <button type="button" className={PRIMARY} disabled={issue.isPending} onClick={() => issue.mutate()}>
              {issue.isPending ? 'Issuing…' : 'Issue address'}
            </button>
          </FormFoot>
        </>
      ) : (
        <>
          <h2 className="text-title font-semibold">Issued · {fresh.label}</h2>
          <GiveOut address={fresh.address} check={check!} label="the new address" />
          <div className="border-t border-stroke-soft">
            <Fact label="Network">USDT·TRC</Fact>
            <Fact label="Path"><span className="font-mono">{fresh.derivation}</span></Fact>
            <Fact label="Customer">Untagged</Fact>
          </div>
          {/* No Tag beside a Mismatch (Screens and Astra, 27 Oct 2026); the server refuses it too (address_mismatch). */}
          <FormFoot note={check === 'mismatch' ? undefined : 'Tag the customer when the money lands'}>
            {check !== 'mismatch' && <Link to={`${CA.addresses}&address=${fresh.id}`} className={QUIET}>Tag customer</Link>}
            <button type="button" className={QUIET} onClick={() => issue.reset()}>Issue another</button>
          </FormFoot>
        </>
      )}
    </FormFrame>
  );
}
